// components/parcours-client/PaymentStep.js
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Logos
import blikLogo from "../../assets/blik-logo.png";
import visaLogo from "../../assets/visa-logo.png";

const API_BASE = import.meta?.env?.VITE_API_URL || process.env.REACT_APP_VENDOR_SYB_API || "";

/* Helpers */
const parseNrAdresse = (str) => {
  if (!str || typeof str !== "string") return { streetNumber: "", localNumber: "" };
  const [nrRue = "", nrLocal = ""] = String(str).split("/").map((s) => s.trim());
  return { streetNumber: nrRue, localNumber: nrLocal };
};
const toNumber = (val) => {
  if (val == null || val === "") return null;
  const n = parseFloat(String(val).replace(",", "."));
  return Number.isNaN(n) ? null : n;
};
const clampBillingDay = (d) => {
  const n = Number(d);
  if (!Number.isFinite(n)) return null;
  return Math.max(1, Math.min(28, n));
};

/* Carte de sélection */
function PaymentMethodCard({ selected, onSelect, title, subtitle, logo, badges = [], footer, children }) {
  return (
    <Paper
      onClick={onSelect}
      elevation={selected ? 6 : 1}
      sx={{
        width: "100%",
        p: 3,
        borderRadius: 3,
        cursor: "pointer",
        border: (theme) => `2px solid ${selected ? theme.palette.success.main : "transparent"}`,
        boxShadow: (theme) => (selected ? `0 0 0 4px ${theme.palette.success.light}33` : undefined),
        transition: "all .2s ease",
        "&:hover": { boxShadow: selected ? 6 : 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {logo}
        <Box>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
      </Stack>

      {badges?.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {badges.map((b, i) => (
            <Chip key={i} label={b} size="small" />
          ))}
        </Stack>
      )}

      <Box>{children}</Box>

      {footer && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">{footer}</Typography>
        </>
      )}
    </Paper>
  );
}

/* Composant principal */
export default function PaymentStep({ formData, handleBack }) {
  const { t } = useTranslation("parcours-client/PaymentStep");
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: null, text: "" });
  const [blikDialogOpen, setBlikDialogOpen] = useState(false);
  const [blikCode, setBlikCode] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(""); // "blik" | "card"

  const RECURRING_REQUIRED = true; // récurrence obligatoire

  const basePayload = useMemo(() => {
    const { streetNumber, localNumber } = parseNrAdresse(formData?.nrAdresse);
    return {
      subscriber: {
        pesel: formData?.pesel || "",
        firstName: (formData?.prenom || "").trim(),
        lastName: (formData?.nom || "").trim(),
        email: formData?.email || "",
        phone: formData?.telephone || "",
        address: {
          street: formData?.adresse || "",
          streetNumber,
          localNumber,
          postalCode: formData?.codePostal || "",
          city: formData?.ville || "",
          country: formData?.pays || "",
        },
      },
      vehicle: {
        type: formData?.bikeType || "",
        brand: formData?.marque || "",
        model: formData?.modele || "",
        price: toNumber(formData?.prix),
        purchaseDate: formData?.dateAchat || "",
        serial: formData?.numeroSerie || "",
      },
      subscription: {
        insuranceId: formData?.assurance?.id || null,
        product: formData?.assurance
          ? {
              name: formData.assurance.nom,
              description: formData.assurance.description,
              priceMonthly: String(formData.assurance.prix ?? ""),
              termMonths: Number(formData.assurance.engagement ?? 0) || null,
              coverage: formData.assurance.features || [],
              coverageLimit:
                formData.assurance.priseEnCharge != null ? String(formData.assurance.priseEnCharge) : null,
              productCode: formData.assurance.product_code || null,
            }
          : null,
        selectedDay: clampBillingDay(formData?.selectedDay),
        startDate: formData?.startDate || null,
      },
      signature: formData?.signature || null,
    };
  }, [formData]);

  const validate = (payload) => {
    if (!payload.subscription?.insuranceId) return t("errors.noInsurance");
    if (!payload.subscriber?.email) return t("errors.needEmail");
    if (!payload.vehicle?.price) return t("errors.needPrice");
    if (!payload.subscription?.startDate) return t("errors.needStartDate");
    if (!payload.subscription?.selectedDay) return t("errors.needBillingDay");
    return null;
  };

  // Sauvegarde du dernier payload pour pouvoir relancer plus tard
  const saveLastPayload = (payload) => {
     try {
       sessionStorage.setItem(
         "syb:lastPaymentPayload",
         JSON.stringify({ payload, ts: Date.now() })
       );
      const email = payload?.subscriber?.email || "";
      if (email) localStorage.setItem("syb:lastPaymentEmail", email);
     } catch {}
   };

  // Appelle le back → renvoie l'objet data.data (inclut subscriptionId, paymentUrl éventuel)
  const startSubscription = async (method, extraData = {}) => {
    setMsg({ type: null, text: "" });

    const forcedExtra =
      method === "blik"
        ? { recurring: RECURRING_REQUIRED, ...extraData }
        : method === "card"
        ? { saveCard: RECURRING_REQUIRED, ...extraData }
        : extraData;

    const payload = { ...basePayload, payment: { method, ...forcedExtra } };
    const err = validate(payload);
    if (err) {
      setMsg({ type: "error", text: err });
      return null;
    }

    // ➜ mémoriser avant l'appel pour permettre le "Relancer le paiement"
    saveLastPayload(payload);

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/subscriptions/by-customer`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      return data?.data || null;
    } catch (e) {
      console.error(e?.response?.data || e);
      setMsg({
        type: "error",
        text: e?.response?.data?.message || e?.message || t("errors.generic"),
      });
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  // Redirections: ouvrir Tpay dans un nouvel onglet + route SPA /paiement/en-cours
  const handleCardFlow = (out) => {
    const sid = out?.subscriptionId;
    if (out?.paymentUrl) {
      window.open(out.paymentUrl, "_blank", "noopener,noreferrer");
    }
    if (sid) {
      navigate(`/paiement/en-cours?sid=${encodeURIComponent(sid)}`);
    } else if (!out?.paymentUrl) {
      setMsg({ type: "error", text: t("errors.generic") });
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedMethod) {
      setMsg({ type: "error", text: t("errors.chooseMethod") });
      return;
    }
    if (selectedMethod === "blik") {
      setBlikDialogOpen(true);
      return;
    }
    if (selectedMethod === "card") {
      const out = await startSubscription("card");
      if (out) handleCardFlow(out);
    }
  };

  const monthlyPrice = formData?.assurance?.prix != null ? Number(formData.assurance.prix) : null;
  const engagement = formData?.assurance?.engagement != null ? Number(formData.assurance.engagement) : null;

  const blikBadges = [t("badges.autodebit"), t("badges.noCard")];
  const cardBadges = [t("badges.autodebit"), t("badges.tokenize")];

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 6 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 920,
          borderRadius: 3,
          px: { xs: 3, md: 6 },
          py: { xs: 5, md: 6 },
          backgroundColor: "#F8F8F8",
          border: "1px solid #eee",
        }}
      >
        <Stack spacing={1} mb={2}>
          <Typography variant="h5" fontWeight={800}>{t("title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("lead")}
          </Typography>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t("notice.prefix")} <b>{t("notice.mandatory")}</b>.
          {monthlyPrice != null && <> {t("notice.amount", { amount: monthlyPrice.toFixed(2) })}.</>}
          {engagement ? <> {t("notice.engagement", { months: engagement })}.</> : null}
        </Alert>

        <Stack spacing={2} mb={2}>
          <PaymentMethodCard
            selected={selectedMethod === "blik"}
            onSelect={() => setSelectedMethod("blik")}
            title="BLIK"
            subtitle={t("blik.subtitle")}
            logo={<Box component="img" src={blikLogo} alt="BLIK" sx={{ width: 56, height: 28, objectFit: "contain" }} />}
            badges={blikBadges}
            footer={t("blik.footer")}
          >
            <Typography variant="body2" color="text.secondary">
              {t("blik.body")}
            </Typography>
          </PaymentMethodCard>

          <PaymentMethodCard
            selected={selectedMethod === "card"}
            onSelect={() => setSelectedMethod("card")}
            title={t("card.title")}
            subtitle="Visa / Mastercard"
            logo={
              <Stack direction="row" spacing={1} alignItems="center">
                <Box component="img" src={visaLogo} alt="Visa" sx={{ width: 52, height: 18, objectFit: "contain" }} />
                <Chip size="small" label="Mastercard" />
              </Stack>
            }
            badges={cardBadges}
            footer={t("card.footer")}
          >
            <Typography variant="body2" color="text.secondary">
              {t("card.body")}
            </Typography>
          </PaymentMethodCard>
        </Stack>

        {msg.type && (
          <Alert severity={msg.type} sx={{ mb: 2 }}>
            {msg.text}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" align="center" sx={{ display: "block", mb: 1.5 }}>
          {t("secureLegend")}
        </Typography>

        <Box sx={{ position: "relative", minHeight: 48 }}>
          {/* Bouton Retour à gauche */}
          <Box sx={{ position: "absolute", left: 0, top: 0 }}>
            <Button variant="outlined" onClick={handleBack} disabled={submitting}>
              {t("actions.back")}
            </Button>
          </Box>

          {/* Bouton Confirmer centré */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title={!selectedMethod ? t("errors.chooseMethod") : ""} placement="top">
              <span>
                <Button
                  variant="contained"
                  onClick={handleConfirmPayment}
                  disabled={!selectedMethod || submitting}
                  sx={{ borderRadius: 2, px: 3, color: "#fff" }}
                >
                  {submitting ? <CircularProgress size={18} color="inherit" /> : t("actions.confirm")}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Popup BLIK */}
      <Dialog open={blikDialogOpen} onClose={() => setBlikDialogOpen(false)}>
        <DialogTitle>{t("blik.modal.title")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("blik.modal.label")}
            type="tel"
            fullWidth
            value={blikCode}
            onChange={(e) => setBlikCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
          />
          <Typography variant="caption" color="text.secondary">
            {t("blik.modal.hint")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlikDialogOpen(false)}>{t("actions.cancel")}</Button>
          <Button
            onClick={async () => {
              if (blikCode.length === 6) {
                setBlikDialogOpen(false);
                const out = await startSubscription("blik", { blikToken: blikCode });
                const sid = out?.subscriptionId;
                if (out?.paymentUrl) {
                  window.open(out.paymentUrl, "_blank", "noopener,noreferrer");
                }
                if (sid) {
                  navigate(`/paiement/en-cours?sid=${encodeURIComponent(sid)}`);
                } else {
                  setMsg({ type: "info", text: t("errors.generic") });
                }
              }
            }}
            disabled={blikCode.length !== 6 || submitting}
            variant="contained"
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : t("actions.validate")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}